import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import * as Sentry from "@sentry/react";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { clearAllCaches } from "@/utils/cacheAndVersion";
import { getFormInstance } from "@/utils/form";
import { ErrorView, FormView, SuccessView } from "@/views";
import LoadingView from "@/views/LoadingView";

import type { PublicFormInstance } from "@/utils/form";

const Home = () => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const [formInstance, setFormInstance] = useState<PublicFormInstance | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [formError, setFormError] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [clearingCache, setClearingCache] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [_, setTimer] = useState(30);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleReset = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTimer(30);

    setIsCompleted(false);
    setFormError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getFields = useCallback(async () => {
    setIsLoading(true);
    const instance = await getFormInstance();

    if (!instance) {
      setFormError({
        title: "Could not load survey form",
        message: "Please refresh the page and try again.",
      });
      return;
    }

    setFormInstance(instance);
    setIsLoading(false);
  }, []);

  const onSuccess = useCallback(
    (message?: string) => {
      if (message) {
        setFormError({
          title: "Survey data has been submitted but:",
          message,
        });
      }

      setIsCompleted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });

      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev === 0) return 0;
          const newTimer = prev - 1;
          if (newTimer === 0) {
            handleReset();
          }
          return newTimer;
        });
      }, 1000);
    },
    [handleReset],
  );

  const handleClearCache = async () => {
    setClearingCache(true);
    const alertResponse = window.confirm(
      "Are you sure you want to clear the cache? The page will be reloaded and you will lose any unsaved data.",
    );
    if (alertResponse) {
      try {
        await clearAllCaches();
        window.location.reload();
      } catch (error: unknown) {
        console.error("Error clearing cache:", error);
        Sentry.captureException(error);
        setClearingCache(false);
        setFormError({
          title: "Could not clear cache.",
          message: (error as Error).message,
        });
      }
    } else {
      setClearingCache(false);
    }
  };

  const currentView = useMemo(() => {
    if (isLoading) return "loading";
    if (isCompleted) return "success";
    if (formError) return "error";
    return "form";
  }, [isCompleted, formError, isLoading]);

  useEffect(() => {
    if (formError) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const errorMessage = document.getElementById("error-message");
          if (errorMessage) {
            errorMessage.scrollIntoView({ behavior: "smooth" });
          }
        });
      });
    }
  }, [formError]);

  useEffect(() => {
    getFields();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <main>
      <Header />
      <div className="p-[30px] min-h-[calc(100dvh-290px)]">
        <SwitchTransition>
          <CSSTransition
            key={currentView}
            nodeRef={nodeRef}
            timeout={300}
            classNames={"fade"}
            unmountOnExit
          >
            <div ref={nodeRef}>
              {currentView === "loading" && <LoadingView />}

              {currentView === "success" && <SuccessView />}

              {currentView === "form" && (
                <FormView
                  formInstance={formInstance}
                  onSuccess={onSuccess}
                  onError={setFormError}
                />
              )}
            </div>
          </CSSTransition>
        </SwitchTransition>

        {formError && (
          <ErrorView
            formError={formError}
            clearingCache={clearingCache}
            onClearCache={handleClearCache}
          />
        )}
      </div>
      <Footer />
    </main>
  );
};

export default Home;
